import { AxiosError } from 'axios';
import { UseMutationResult, useMutation, useQuery, useQueryClient } from 'react-query';

import registrationService from 'services/dvrsService';
import {
  OTPDeviceParams,
  OTPDeviceResponse,
  RegisterDeviceParams,
  RegisterDeviceResponse
} from 'services/dvrsService/dvrsService';
import { RegisterResponseState, RegisteredLocation } from 'types/appState';
import { QUERY_KEY } from 'utils/constants';

type AuthenticateDeviceWithOTP = UseMutationResult<
  OTPDeviceResponse,
  AxiosError,
  Omit<OTPDeviceParams, 'fp'>,
  unknown
>;

type RegisterDevice = UseMutationResult<
  RegisterDeviceResponse,
  AxiosError,
  Omit<RegisterDeviceParams, 'fp'>,
  unknown
>;

interface UseRegisteredLocations {
  authenticateDeviceWithOTP: AuthenticateDeviceWithOTP;
  registerDevice: RegisterDevice;
  registerDeviceResponse: RegisterResponseState | undefined;
}

interface UseRegisteredLocationsParams {
  onRegisterDeviceSuccess?: () => void;
  onAuthenticateDeviceWithOTPSuccess?: () => void;
}

const useDeviceRegistration = ({
  onRegisterDeviceSuccess,
  onAuthenticateDeviceWithOTPSuccess
}: UseRegisteredLocationsParams = {}): UseRegisteredLocations => {
  const queryClient = useQueryClient();

  const authenticateDeviceWithOTP: AuthenticateDeviceWithOTP = useMutation(
    params => registrationService.authenticateDeviceWithOTP(params),
    {
      onSuccess: ({ email, ...newLocation }) => {
        queryClient.removeQueries([QUERY_KEY.RegisterResponse]);
        queryClient.setQueryData<RegisteredLocation[]>(
          QUERY_KEY.RegisteredLocations,
          registeredLocations =>
            (registeredLocations ?? [])
              .filter(location => location.locationId !== newLocation.locationId)
              .map(location => ({ ...location, isSelected: false }))
              .concat({ ...newLocation, isSelected: true })
        );

        onAuthenticateDeviceWithOTPSuccess?.();
      }
    }
  );

  const registerDevice: RegisterDevice = useMutation(
    params => registrationService.registerDevice(params),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData<RegisterResponseState>(QUERY_KEY.RegisterResponse, {
          ...data,
          kioskCode: variables.kioskCode
        });

        onRegisterDeviceSuccess?.();
      }
    }
  );

  const { data: registerResponse } = useQuery<unknown, unknown, RegisterResponseState>(
    QUERY_KEY.RegisterResponse,
    {
      enabled: false
    }
  );

  return {
    authenticateDeviceWithOTP,
    registerDevice,
    registerDeviceResponse: registerResponse
  };
};

export default useDeviceRegistration;
